import { syntaxTree } from "@codemirror/language";
import { type EditorState, RangeSetBuilder } from "@codemirror/state";
import {
  Decoration,
  type DecorationSet,
  type EditorView,
  ViewPlugin,
  type ViewUpdate,
} from "@codemirror/view";

// Emphasis for calls to known helper names (mstream's `result`,
// `fill_schema`, …): a mark over the callee of every CallExpression whose
// name is in the set. Highlight tags are per node type, so a name-based
// style has to be a decoration.

/** The callee ranges of calls to `names` within [from, to). */
export function knownCallRanges(
  state: EditorState,
  names: ReadonlySet<string>,
  from = 0,
  to = state.doc.length,
): { from: number; to: number }[] {
  const out: { from: number; to: number }[] = [];
  syntaxTree(state).iterate({
    from,
    to,
    enter: (node) => {
      if (node.name !== "CallExpression") return;
      const callee = node.node.firstChild;
      if (callee?.name !== "VariableName") return;
      if (names.has(state.sliceDoc(callee.from, callee.to)))
        out.push({ from: callee.from, to: callee.to });
    },
  });
  return out;
}

// The marks follow the text, the viewport and the (incrementally
// finishing) parse.
function needsRebuild(update: ViewUpdate): boolean {
  if (update.docChanged || update.viewportChanged) return true;
  return syntaxTree(update.startState) !== syntaxTree(update.state);
}

export function knownFunctionMarks(
  names: ReadonlySet<string>,
  className: string,
) {
  const mark = Decoration.mark({ class: className });
  const build = (view: EditorView): DecorationSet => {
    const builder = new RangeSetBuilder<Decoration>();
    for (const { from, to } of view.visibleRanges) {
      for (const range of knownCallRanges(view.state, names, from, to))
        builder.add(range.from, range.to, mark);
    }
    return builder.finish();
  };
  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = build(view);
      }
      update(update: ViewUpdate) {
        if (needsRebuild(update)) this.decorations = build(update.view);
      }
    },
    { decorations: (plugin) => plugin.decorations },
  );
}
